import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  arrayRemove,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  increment,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { app, db, API_BASE_URL } from '../../firebase/firebaseConfig';
import {
  DetailShell,
  HCard,
  HearthIcon,
  HigloIcon,
  SignalBars,
  HBadge,
  SerifHeading,
  SectionLabel,
  HField,
  HGhostButton,
  HSecondaryButton,
  HPrimaryButton,
  HDestructiveButton,
  HButtonPair,
} from '../h';
import { Switch } from '../ui/switch';
import { SmsNotificationsCard } from './SmsNotificationsCard';
import { useFamiliesData } from './useFamiliesData';
import { useMock } from './mockContext';
import { FactoryResetDialog } from './FactoryResetDialog';
import { cn } from '../../lib/utils';

/* v9 mockups 4.2 (online), 4.3 (offline — was set up before), 4.4 (never set up).

   Structurally these three are the same screen — a yellow status banner is
   the only thing that differs between 4.3/4.4. We branch on:
     - hasEverSignedIn === false  → "Ready to setup?" banner (4.4 — never set up)
     - hasEverSignedIn && !isOnline → "This Hearth isn't reaching us" (4.3 — offline)
     - hasEverSignedIn && isOnline → no banner (4.2)
*/
export function HearthDetail({
  accountUUID,
  primaryFamilyID,
  // `owningFamilies` used to gate owner-only sections here, but after the
  // isOwner gating was removed (co-managers see the same Hearth detail UI
  // as owners) this prop is unused. Kept in the destructure to accept it
  // from AppShell without a React "unknown prop" warning.
  // eslint-disable-next-line no-unused-vars
  owningFamilies,
  families: accountFamilies,
  userEmail,
  familyID: familyIDProp,
}) {
  const params = useParams();
  // Production AppShell route supplies familyID via useParams; previews can
  // pass it directly so we don't need a wrapping Route.
  const familyID = familyIDProp || params.familyID;
  const navigate = useNavigate();
  const familiesData = useFamiliesData([familyID]);
  const family = familiesData[familyID] || {};
  const isPrimary = familyID === primaryFamilyID;

  const [renaming, setRenaming] = useState(false);
  const [pendingName, setPendingName] = useState('');
  const [coManagerEntries, setCoManagerEntries] = useState([]);
  // `ownerUUIDs === null` means "still loading". We don't render the
  // Co-Manager rows until we know owners, otherwise the current account's
  // own row briefly renders as a co-manager with a Give-Up button before
  // the owners subcollection resolves.
  const [ownerUUIDs, setOwnerUUIDs] = useState(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [savingPrimary, setSavingPrimary] = useState(false);
  const [savingAutoMode, setSavingAutoMode] = useState(false);
  const [autoModeConfirm, setAutoModeConfirm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inviting, setInviting] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [coManagerError, setCoManagerError] = useState('');
  const renameRef = useRef(null);

  const mock = useMock();

  /* Live list of controllers on this Hearth — includes both accepted
     (UUID-keyed) and pending (email-keyed) entries, plus the signed-in
     user (self) so the "Give Up Control" row renders with the right
     badges. Name + email for UUID-keyed entries comes from
     /user/getEmailForControllerUUID (the OLD DeviceSettings pattern) —
     cross-account `accountsInfo` reads are blocked by security rules, so
     a direct getDoc would silently return nothing and rows would render
     nameless. Generation counter discards stale async results if the
     snapshot changes mid-fetch. */
  const controllersGenRef = useRef(0);
  useEffect(() => {
    if (mock?.coManagers) {
      setCoManagerEntries(mock.coManagers);
      return undefined;
    }
    if (!familyID) return undefined;

    const unsub = onSnapshot(
      collection(db, 'familyInfo', familyID, 'controllers'),
      async (snap) => {
        const generation = ++controllersGenRef.current;

        const list = [];
        const uuidsToResolve = [];
        for (const d of snap.docs) {
          const id = d.id;
          if (id.includes('@')) {
            list.push({
              id,
              isPending: true,
              isUUIDBased: false,
              email: id,
              name: null,
            });
          } else {
            uuidsToResolve.push(id);
            list.push({
              id,
              isPending: false,
              isUUIDBased: true,
              email: null,
              name: null,
            });
          }
        }

        // Render immediately with what we have so the UI doesn't wait on
        // the network round-trip.
        setCoManagerEntries(list);

        if (uuidsToResolve.length === 0) return;
        try {
          const auth = getAuth(app);
          const user = auth.currentUser;
          if (!user) return;
          const idToken = await user.getIdToken();
          const results = await Promise.all(
            uuidsToResolve.map(async (uuid) => {
              try {
                const res = await fetch(
                  `${API_BASE_URL}/user/getEmailForControllerUUID`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${idToken}`,
                    },
                    body: JSON.stringify({ controllerUUID: uuid }),
                  },
                );
                if (res.ok) {
                  const data = await res.json();
                  return { uuid, email: data.email || null, name: data.name || null };
                }
              } catch (err) {
                console.warn('getEmailForControllerUUID error', uuid, err);
              }
              return { uuid, email: null, name: null };
            }),
          );

          // Discard if a newer snapshot fired while we were awaiting.
          if (generation !== controllersGenRef.current) return;

          setCoManagerEntries((prev) => prev.map((entry) => {
            if (!entry.isUUIDBased) return entry;
            const hit = results.find((r) => r.uuid === entry.id);
            if (!hit) return entry;
            return { ...entry, email: hit.email, name: hit.name };
          }));
        } catch (err) {
          console.warn('resolve controllers batch error', err);
        }
      },
      (err) => console.warn('controllers listener error', err.code),
    );
    return () => unsub();
  }, [familyID, mock]);

  /* One-shot load of owners — rarely changes, mirrors OLD DeviceSettings:176–181.
     On error we still leave `ownerUUIDs` as an empty array so the Co-Managers
     section isn't stuck on a loading placeholder forever. */
  useEffect(() => {
    if (mock?.coManagers) { setOwnerUUIDs([]); return; }
    if (!familyID) return;
    setOwnerUUIDs(null);
    getDocs(collection(db, 'familyInfo', familyID, 'owners'))
      .then((snap) => setOwnerUUIDs(snap.docs.map((d) => d.id)))
      .catch((err) => {
        console.warn('owners load error', err.code);
        setOwnerUUIDs([]);
      });
  }, [familyID, mock]);

  /* Active controllers are non-pending (UUID-keyed). "Only controller" is
     true when there's at most one of them left — gates the Give Up button
     the same way OLD DeviceSettings does. */
  const activeControllerCount = coManagerEntries.filter((c) => !c.isPending).length;
  const isOnlyController = activeControllerCount <= 1;

  const handleMakePrimary = useCallback(
    async (next) => {
      if (!accountUUID) return;
      // We don't allow un-toggling primary directly from here — every account
      // needs exactly one primary. The user changes primary by toggling on a
      // different Hearth. So `next === true` is the only meaningful state.
      if (!next) return;
      setSavingPrimary(true);
      try {
        await updateDoc(doc(db, 'accountsInfo', accountUUID), {
          primaryFamilyID: familyID,
        });
      } catch (err) {
        console.error('makePrimary error', err);
      } finally {
        setSavingPrimary(false);
      }
    },
    [accountUUID, familyID],
  );

  /* Auto Mode toggle. Matches OLD IndividualDashboard.updateAutoMode
     (lines 743–791):
       - Enabling with any non-Centre positions currently set → show a
         confirm modal ("these Higlos will be deactivated").
       - On confirm (or if no non-Centre positions): clear position +
         activeIgloos for every non-Centre igloo, then write autoMode=true.
       - Turning off: just flip the flag.
     Disabling never needs confirmation. */
  const nonCentreIglooLinks = (family.iglooLinks || []).filter(
    (link) => link.position && link.position !== 'Centre',
  );

  const applyAutoMode = useCallback(
    async (enabled) => {
      setSavingAutoMode(true);
      try {
        if (enabled && nonCentreIglooLinks.length > 0) {
          await Promise.all(
            nonCentreIglooLinks.map(async (link) => {
              await updateDoc(
                doc(db, 'familyInfo', familyID, 'igloos', link.iglooID),
                { position: deleteField() },
              );
              await deleteDoc(
                doc(db, 'familyInfo', familyID, 'activeIgloos', link.iglooID),
              ).catch(() => {});
            }),
          );
        }
        await updateDoc(doc(db, 'familyInfo', familyID), { autoMode: enabled });
      } catch (err) {
        console.error('autoMode error', err);
      } finally {
        setSavingAutoMode(false);
        setAutoModeConfirm(false);
      }
    },
    [familyID, nonCentreIglooLinks],
  );

  const handleAutoMode = useCallback(
    async (next) => {
      if (next && nonCentreIglooLinks.length > 0) {
        setAutoModeConfirm(true);
        return;
      }
      applyAutoMode(!!next);
    },
    [applyAutoMode, nonCentreIglooLinks.length],
  );

  /* ──────────── Connected Hearths (per-perspective labels) ─────────── */

  /* Build the list of "Hearths this Hearth is connected to" by walking the
     Higlos this Hearth is a member of and unioning iglooInfo.controllers +
     iglooInfo.members across them. The per-perspective label this Hearth
     shows for each is read from `familyInfo/{familyID}/connectedFamilies/
     {otherFID}.name`; the connected Hearth's global name is read from
     `familyInfo/{otherFID}.name`. Both are live-subscribed.

     Writes from this section go to ONLY `familyInfo/{familyID}/connected
     Families/{otherFID}.name` — they affect what this Hearth shows for the
     other Hearth and nothing else. */
  const [iglooMembersByID, setIglooMembersByID] = useState({});
  const [connectedGlobalNames, setConnectedGlobalNames] = useState({});
  const [thisFamilyConnectedLabels, setThisFamilyConnectedLabels] = useState({});

  const iglooLinkKey = (family.iglooLinks || [])
    .map((l) => l.iglooID)
    .sort()
    .join('|');

  useEffect(() => {
    const links = family.iglooLinks || [];
    if (links.length === 0) {
      setIglooMembersByID({});
      return undefined;
    }
    const unsubs = links.map((link) =>
      onSnapshot(
        doc(db, 'iglooInfo', link.iglooID),
        (snap) => {
          if (!snap.exists()) {
            setIglooMembersByID((prev) => {
              const next = { ...prev };
              delete next[link.iglooID];
              return next;
            });
            return;
          }
          const d = snap.data() || {};
          const all = new Set([
            ...(d.controllers || []),
            ...(d.members || []),
          ]);
          setIglooMembersByID((prev) => ({ ...prev, [link.iglooID]: all }));
        },
        (err) => {
          if (err?.code === 'permission-denied') return;
          console.warn('connected hearths iglooInfo listener', link.iglooID, err.code);
        },
      ),
    );
    return () => unsubs.forEach((u) => { try { u(); } catch (_) {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iglooLinkKey]);

  useEffect(() => {
    if (!familyID) return undefined;
    const unsub = onSnapshot(
      collection(db, 'familyInfo', familyID, 'connectedFamilies'),
      (snap) => {
        const next = {};
        snap.docs.forEach((d) => {
          const data = d.data() || {};
          if (data.name) next[d.id] = data.name;
        });
        setThisFamilyConnectedLabels(next);
      },
      (err) => {
        if (err?.code === 'permission-denied') return;
        console.warn('connectedFamilies listener', familyID, err.code);
      },
    );
    return () => { try { unsub(); } catch (_) {} };
  }, [familyID]);

  const connectedFamilyIDs = useMemo(() => {
    const set = new Set();
    Object.values(iglooMembersByID).forEach((members) => {
      members.forEach((fid) => {
        if (fid && fid !== familyID) set.add(fid);
      });
    });
    return Array.from(set).sort();
  }, [iglooMembersByID, familyID]);

  const connectedKey = connectedFamilyIDs.join('|');

  useEffect(() => {
    if (connectedFamilyIDs.length === 0) return undefined;
    // Add-only: never unsubscribe in the lifetime of the screen — a name we
    // already showed shouldn't suddenly go blank because a Higlo membership
    // changed.
    const toAdd = connectedFamilyIDs.filter((fid) => !(fid in connectedGlobalNames));
    if (toAdd.length === 0) return undefined;
    const unsubs = toAdd.map((fid) =>
      onSnapshot(
        doc(db, 'familyInfo', fid),
        (snap) => {
          if (snap.exists()) {
            setConnectedGlobalNames((prev) => ({
              ...prev,
              [fid]: snap.data().name || '',
            }));
          }
        },
        (err) => {
          if (err?.code === 'permission-denied') return;
          console.warn('connected hearth name listener', fid, err.code);
        },
      ),
    );
    return () => unsubs.forEach((u) => { try { u(); } catch (_) {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedKey]);

  const [editingConnectedFID, setEditingConnectedFID] = useState(null);
  const [pendingConnectedLabel, setPendingConnectedLabel] = useState('');
  const [savingConnectedLabel, setSavingConnectedLabel] = useState(false);

  const beginConnectedRename = (otherFID) => {
    const current =
      thisFamilyConnectedLabels[otherFID] || connectedGlobalNames[otherFID] || '';
    setEditingConnectedFID(otherFID);
    setPendingConnectedLabel(current);
  };
  const cancelConnectedRename = () => {
    setEditingConnectedFID(null);
    setPendingConnectedLabel('');
  };
  const saveConnectedRename = async () => {
    if (!editingConnectedFID) return;
    const trimmed = (pendingConnectedLabel || '').trim();
    if (!trimmed) {
      cancelConnectedRename();
      return;
    }
    setSavingConnectedLabel(true);
    try {
      await setDoc(
        doc(db, 'familyInfo', familyID, 'connectedFamilies', editingConnectedFID),
        { name: trimmed },
        { merge: true },
      );
      cancelConnectedRename();
    } catch (err) {
      console.error('connected rename error', err);
    } finally {
      setSavingConnectedLabel(false);
    }
  };

  /* Rename has two phases:
       1. attemptRename — runs a "smart check" against every other family in
          the account: do any of them currently know this Hearth by a label
          that differs from both the current global name AND the proposed
          new name? If so, surface a warning before overwriting; otherwise
          commit silently.
       2. doCommitRename — fans out:
            - familyInfo/{thisFID}.name
            - familyInfo/{siblingFID}/connectedFamilies/{thisFID}.name (set+merge)
          Same-account siblings only — this account has no authority over
          how other-account hearths know this Hearth.
     A write batch keeps the global update + fan-out atomic.
     No cloud function — Firestore rules permit each write because the
     caller is a controller of every family in their own account. */
  const [savingRename, setSavingRename] = useState(false);
  // null when no conflict; otherwise an array of {siblingFID, siblingName, currentLabel}
  const [conflictPreview, setConflictPreview] = useState(null);

  const beginRename = () => {
    setPendingName(family.familyName || '');
    setConflictPreview(null);
    setRenaming(true);
    setTimeout(() => renameRef.current?.focus(), 0);
  };
  const cancelRename = () => {
    setRenaming(false);
    setPendingName('');
    setConflictPreview(null);
  };

  const doCommitRename = async (trimmed) => {
    setSavingRename(true);
    try {
      const others = (accountFamilies || []).filter((id) => id !== familyID);
      const batch = writeBatch(db);
      batch.update(doc(db, 'familyInfo', familyID), { name: trimmed });
      for (const otherFID of others) {
        batch.set(
          doc(db, 'familyInfo', otherFID, 'connectedFamilies', familyID),
          { name: trimmed },
          { merge: true },
        );
      }
      await batch.commit();
      cancelRename();
    } catch (err) {
      console.error('rename commit error', err);
    } finally {
      setSavingRename(false);
    }
  };

  const attemptRename = async () => {
    const trimmed = (pendingName || '').trim();
    if (!trimmed) {
      cancelRename();
      return;
    }
    const isUnchanged = trimmed === family.familyName;
    const others = (accountFamilies || []).filter((id) => id !== familyID);
    if (others.length === 0) {
      // Single-family account — nothing to fan out to, nothing to unify.
      if (isUnchanged) cancelRename();
      else await doCommitRename(trimmed);
      return;
    }
    setSavingRename(true);
    try {
      // Smart check: for each sibling, look at what they currently know this
      // Hearth as. A sibling whose label is missing or matches the current
      // global name (or the new name) isn't a conflict — we'll just write
      // the new value. A sibling whose label is something else has a custom
      // per-perspective name we'd be overwriting, so warn.
      // We run this even when the value is unchanged from the current global
      // so the user can hit Save without editing to unify a stale per-
      // perspective label across the account.
      const conflicts = [];
      await Promise.all(
        others.map(async (otherFID) => {
          try {
            const cfRef = doc(
              db,
              'familyInfo',
              otherFID,
              'connectedFamilies',
              familyID,
            );
            const cfSnap = await getDoc(cfRef);
            const existingLabel = cfSnap.exists()
              ? cfSnap.data().name || null
              : null;
            if (
              existingLabel
              && existingLabel !== family.familyName
              && existingLabel !== trimmed
            ) {
              const siblingSnap = await getDoc(doc(db, 'familyInfo', otherFID));
              const siblingName = siblingSnap.exists()
                ? siblingSnap.data().name || 'a Hearth'
                : 'a Hearth';
              conflicts.push({
                siblingFID: otherFID,
                siblingName,
                currentLabel: existingLabel,
              });
            }
          } catch (err) {
            console.warn('rename conflict check failed', otherFID, err.code);
          }
        }),
      );
      if (conflicts.length === 0) {
        // Nothing to unify and either no change or a clean rename.
        if (isUnchanged) {
          cancelRename();
          setSavingRename(false);
        } else {
          await doCommitRename(trimmed);
        }
      } else {
        setConflictPreview(conflicts);
        setSavingRename(false);
      }
    } catch (err) {
      console.error('attemptRename error', err);
      setSavingRename(false);
    }
  };

  const handleRepeatTraining = async () => {
    try {
      await updateDoc(doc(db, 'familyInfo', familyID), { trainingComplete: false });
    } catch (err) {
      console.error('repeat training error', err);
    }
  };

  const handleFactoryReset = async () => {
    try {
      await updateDoc(doc(db, 'familyInfo', familyID), { signoutRequested: true });
      setShowResetDialog(false);
    } catch (err) {
      console.error('factory reset error', err);
    }
  };

  /* Factory reset gating mirrors OLD IndividualDashboard:
       isOffline = hasPinged && !isOnline
     The button is disabled only when the device has pinged before but is
     currently unreachable — otherwise the signoutRequested write has
     nowhere to land. A never-signed-in Hearth leaves the button enabled
     (the flag waits on the device's first sign-in). */
  const resetDisabled = family.hasEverSignedIn === true && !family.isOnline;
  const resetDisabledReason =
    'This Hearth is offline so it can\u2019t receive the reset command. '
    + 'To reset, either connect to WiFi, or use hand control on the '
    + '"No Network Connection" screen to reset.';

  /* ──────────── Co-Manager invite / cancel / give-up ─────────── */

  /* Invite a co-manager by email — matches OLD
     DeviceSettings.inviteController (246–265): three parallel writes to
     controllers/{email}, pendingControllerInvites/{email}, and
     familyInviteMessages/{email}/familyIDs/{familyID} with a
     'controller' type + serverTimestamp. */
  const inviteCoManager = async () => {
    const email = (inviteEmail || '').trim().toLowerCase();
    setInviteError('');
    if (!email) { setInviteError('Please enter an email address.'); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setInviteError('That doesn\u2019t look like an email address.');
      return;
    }
    if (email === (userEmail || '').toLowerCase()) {
      setInviteError('That\u2019s your own email.');
      return;
    }
    // Soft dedupe client-side — Firestore rules allow the overwrite but the
    // UX is cleaner if we catch it here.
    if (coManagerEntries.some((c) => c.isPending && c.email === email)) {
      setInviteError('You\u2019ve already invited that email.');
      return;
    }
    setInviting(true);
    try {
      await Promise.all([
        setDoc(doc(db, 'familyInfo', familyID, 'controllers', email), {}),
        setDoc(doc(db, 'familyInfo', familyID, 'pendingControllerInvites', email), {}),
        setDoc(
          doc(db, 'familyInviteMessages', email, 'familyIDs', familyID),
          { type: 'controller', timestamp: serverTimestamp() },
        ),
      ]);
      setInviteEmail('');
      setShowInviteForm(false);

      // Fire-and-forget: ask the cloud function to send the invite email.
      // The Firestore invite above is the source of truth; the email is a
      // side effect, so we don't surface failures to the user here.
      try {
        const user = getAuth(app).currentUser;
        if (user) {
          const idToken = await user.getIdToken();
          const response = await fetch(`${API_BASE_URL}/user/sendCoManagerInvite`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ familyID, inviteEmail: email }),
          });
          if (!response.ok) {
            const body = await response.text();
            console.warn(`sendCoManagerInvite returned ${response.status}: ${body}`);
          }
        }
      } catch (emailError) {
        console.warn('Failed to send co-manager invite email:', emailError);
      }
    } catch (err) {
      console.error('inviteCoManager error', err);
      setInviteError('Couldn\u2019t send the invite. Try again?');
    } finally {
      setInviting(false);
    }
  };

  /* Cancel a pending invite — matches OLD DeviceSettings.cancelInvite
     (267–279): three deletes. */
  const cancelCoManagerInvite = async (email) => {
    setCoManagerError('');
    try {
      await Promise.all([
        deleteDoc(doc(db, 'familyInfo', familyID, 'controllers', email)),
        deleteDoc(doc(db, 'familyInfo', familyID, 'pendingControllerInvites', email)),
        deleteDoc(doc(db, 'familyInviteMessages', email, 'familyIDs', familyID)),
      ]);
    } catch (err) {
      console.error('cancelCoManagerInvite error', err);
      setCoManagerError('Couldn\u2019t cancel the invite. Try again?');
    }
  };

  /* Co-manager gives up control of this Hearth — matches OLD
     DeviceSettings.giveUpControl (281–320). Owner and primary-Hearth
     guards mirror the old checks; a co-manager cannot give up their
     primary Hearth, and an owner cannot give up a Hearth they own. */
  const giveUpControl = async () => {
    setCoManagerError('');
    if (!accountUUID) {
      setCoManagerError('Sign in again to give up control.');
      return;
    }
    try {
      const ownerDoc = await getDoc(
        doc(db, 'familyInfo', familyID, 'owners', accountUUID),
      );
      if (ownerDoc.exists()) {
        setCoManagerError('You can\u2019t give up a Hearth you own.');
        return;
      }
      const accountRef = doc(db, 'accountsInfo', accountUUID);
      const accountSnap = await getDoc(accountRef);
      if (accountSnap.exists() && accountSnap.data().primaryFamilyID === familyID) {
        setCoManagerError('You can\u2019t give up your primary Hearth.');
        return;
      }
      await updateDoc(accountRef, {
        families: arrayRemove(familyID),
        numOfFamilies: increment(-1),
        numOfInvitedFamilies: increment(-1),
      });
      await Promise.all([
        deleteDoc(doc(db, 'familyInfo', familyID, 'controllers', accountUUID)),
        userEmail
          ? deleteDoc(doc(db, 'familyInfo', familyID, 'pendingControllerInvites', userEmail))
            .catch(() => {})
          : Promise.resolve(),
        userEmail
          ? deleteDoc(doc(db, 'familyInviteMessages', userEmail, 'familyIDs', familyID))
            .catch(() => {})
          : Promise.resolve(),
      ]);
      navigate('/app/hearths');
    } catch (err) {
      console.error('giveUpControl error', err);
      setCoManagerError('Couldn\u2019t give up control. Try again?');
    }
  };

  const status = resolveHearthStatus(family);

  return (
    <DetailShell
      onBack={() => navigate('/app/hearths')}
      breadcrumb="Hearths"
      rightSlot={
        /* WiFi management replaced Rename here (PLAN wifi-change-webapp §4.1) —
           rename moved onto the device name itself (pencil affordance below,
           matching the Higlos tab). No capability gate (decision, 19 Aug):
           devices auto-update quickly, and a pre-bus Hearth's exchange simply
           times out — the WiFi page's failure copy explains the update. */
        !renaming ? (
          <HGhostButton
            onClick={() => navigate(`/app/hearths/${familyID}/wifi`)}
            className="text-[12px]"
          >
            Manage WiFi
          </HGhostButton>
        ) : null
      }
    >
      <div className="px-4 pb-6">
        {/* Title row — icon is always solid (v3 visual language). Status
            line below uses a coloured dot + short phrase. */}
        <div className="flex items-start gap-3 mb-3">
          <HearthIcon size="sm" className="mt-1" />
          <div className="flex-1 min-w-0">
            {renaming ? (
              <div className="w-full">
                <input
                  ref={renameRef}
                  value={pendingName}
                  onChange={(e) => setPendingName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !conflictPreview) attemptRename();
                    if (e.key === 'Enter' && conflictPreview) {
                      doCommitRename((pendingName || '').trim());
                    }
                    if (e.key === 'Escape') cancelRename();
                  }}
                  disabled={savingRename}
                  className="font-heading font-medium text-slate-900 text-[26px] leading-[1.15] tracking-[-0.015em] bg-transparent outline-none border-b-2 border-slate-300 focus:border-slate-900 w-full"
                />
                <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
                  Up to 40 characters. Used on every Hearth in your account.
                </p>
                {conflictPreview && (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                    <p className="text-[12px] text-amber-900 font-medium leading-relaxed mb-1">
                      Some of your other Hearths know this Hearth by a different name:
                    </p>
                    <ul className="text-[12px] text-amber-900 leading-relaxed list-disc pl-5 mb-2">
                      {conflictPreview.map((c) => (
                        <li key={c.siblingFID}>
                          <strong>{c.siblingName}</strong> calls this Hearth, {c.currentLabel}
                        </li>
                      ))}
                    </ul>
                    <p className="text-[12px] text-amber-900 leading-relaxed">
                      Renaming will replace those names with &ldquo;{(pendingName || '').trim()}&rdquo;.
                    </p>
                  </div>
                )}
                <HButtonPair className="mt-3">
                  <HSecondaryButton onClick={cancelRename} disabled={savingRename}>
                    Cancel
                  </HSecondaryButton>
                  {conflictPreview ? (
                    <HDestructiveButton
                      onClick={() => doCommitRename((pendingName || '').trim())}
                      disabled={savingRename}
                    >
                      {savingRename ? 'Saving…' : 'Replace all names'}
                    </HDestructiveButton>
                  ) : (
                    <HPrimaryButton onClick={attemptRename} disabled={savingRename}>
                      {savingRename ? 'Saving…' : 'Save'}
                    </HPrimaryButton>
                  )}
                </HButtonPair>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-2">
                  {/* Rename lives ON the name (PLAN wifi-change-webapp §4.1): click
                      to edit, pencil so it's discoverable — the Higlos-tab pattern
                      (HigloRename's clickable heading). All rename logic
                      (attemptRename / doCommitRename, the cross-family smart check)
                      is unchanged; only the trigger moved off the header button. */}
                  <button
                    type="button"
                    onClick={beginRename}
                    className="text-left min-w-0 group"
                    aria-label="Rename this Hearth"
                  >
                    <span className="inline-flex items-start gap-1.5 min-w-0">
                      <SerifHeading
                        size="xl"
                        className="group-hover:underline decoration-slate-200 underline-offset-4"
                      >
                        {family.familyName || '…'}
                      </SerifHeading>
                      <PencilIcon className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-600 mt-2 flex-shrink-0" />
                    </span>
                  </button>
                  {family.isOnline && family.wifiRssiDbm != null && (
                    <span className="mt-2 flex-shrink-0">
                      <SignalBars rssiDbm={family.wifiRssiDbm} />
                    </span>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-1.5 text-[13px] text-slate-600">
                  <span
                    className={cn(
                      'inline-block w-[6px] h-[6px] rounded-full flex-shrink-0',
                      status.active ? 'bg-emerald-500' : 'bg-slate-300',
                    )}
                    aria-hidden="true"
                  />
                  <span>{status.text}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Status banner (4.3 / 4.4) — only when not online */}
        {family.hasEverSignedIn === false && (
          <HCard variant="warn" className="p-4 mb-4 mt-4">
            <SectionLabel className="mb-1 text-amber-800">Ready to setup?</SectionLabel>
            <p className="text-[13px] text-slate-700 leading-relaxed mb-3">
              This Hearth hasn&rsquo;t been set up yet. Plug it in, open the lid, and
              we&rsquo;ll walk you through WiFi and the QR.
            </p>
            <HPrimaryButton
              onClick={() => navigate(`/app/hearths/${familyID}/setup`)}
              className="w-full"
            >
              Set up
            </HPrimaryButton>
          </HCard>
        )}
        {family.hasEverSignedIn === true && !family.isOnline && (
          <HCard variant="warn" className="p-4 mb-4 mt-4">
            <SectionLabel className="mb-1 text-amber-800">
              This Hearth isn&rsquo;t reaching us
            </SectionLabel>
            <p className="text-[13px] text-slate-700 leading-relaxed mb-3">
              It was set up before — if it&rsquo;s been moved or reset, set it up again
              and we&rsquo;ll walk you through WiFi and the QR.
            </p>
            <HPrimaryButton
              onClick={() => navigate(`/app/hearths/${familyID}/setup`)}
              className="w-full"
            >
              Set up again
            </HPrimaryButton>
          </HCard>
        )}

        {/* Preferences are shown to every controller of the Hearth —
            owner or invited co-manager. Setting Primary writes to the
            signed-in account's own `accountsInfo.primaryFamilyID` (per
            OLD IndividualDashboard.handleMakePrimary), which makes this
            Hearth the caller's primary regardless of who owns it. Auto
            Mode is per-Hearth and any controller can flip it, matching
            OLD IndividualDashboard FamilyRow. */}
        {(
          <>
            <SectionLabel className="mb-2 mt-5">Preferences</SectionLabel>
            <HCard className="mb-4">
              <PreferenceRow
                title="Primary Hearth"
                subtitle="Your default for outgoing activity"
                showSubtitle
                control={
                  <Switch
                    checked={isPrimary}
                    onCheckedChange={handleMakePrimary}
                    disabled={savingPrimary || isPrimary /* you can't un-primary from here */}
                    aria-label="Primary Hearth"
                  />
                }
                divider
              />
              <PreferenceRow
                title="Auto Mode"
                subtitle={
                  family.hasEverSignedIn === false
                    ? 'Takes effect when the Hearth is on WiFi.'
                    : !family.isOnline
                      ? 'Takes effect when the Hearth is back online.'
                      : family.autoMode
                        ? (
                          <>
                            Goes straight into your Higlo when opened.{' '}
                            <strong>Hand gestures are turned off</strong> while this is on.
                          </>
                        )
                        : 'You pick a Higlo each time the Hearth wakes up.'
                }
                showSubtitle
                control={
                  <Switch
                    checked={!!family.autoMode}
                    onCheckedChange={handleAutoMode}
                    disabled={savingAutoMode}
                    aria-label="Auto Mode"
                  />
                }
              />
            </HCard>
          </>
        )}

        {/* Co-Managers — any controller (owner or invited co-manager) can
            invite more co-managers, matching OLD DeviceSettings which
            didn't gate the invite form on ownership. */}
        <div className="flex items-end justify-between mb-2">
          <SectionLabel>Co-Managers</SectionLabel>
          <HGhostButton
            onClick={() => {
              setInviteError('');
              setShowInviteForm((s) => !s);
            }}
            className="text-[12px] py-1 px-2"
          >
            {showInviteForm ? 'Close' : '+ Invite'}
          </HGhostButton>
        </div>
        {showInviteForm && (
          <HCard className="p-3 mb-3">
            <HField
              label="Their email"
              placeholder="name@example.com"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              disabled={inviting}
              autoComplete="email"
              className="mb-2"
            />
            {inviteError && (
              <p className="text-[12px] text-red-600 mb-2">{inviteError}</p>
            )}
            <div className="grid grid-cols-2 gap-2">
              <HSecondaryButton
                onClick={() => { setShowInviteForm(false); setInviteEmail(''); setInviteError(''); }}
                disabled={inviting}
              >
                Cancel
              </HSecondaryButton>
              <HPrimaryButton onClick={inviteCoManager} disabled={inviting}>
                {inviting ? 'Sending\u2026' : 'Send invitation'}
              </HPrimaryButton>
            </div>
          </HCard>
        )}
        <HCard className="p-3 mb-4">
          {ownerUUIDs === null ? (
            // Owners subcollection hasn't resolved yet — render a blank row
            // placeholder instead of tentatively painting the current user
            // as a co-manager with a Give-Up button.
            <p className="text-[12px] text-slate-400 text-center py-2">
              &nbsp;
            </p>
          ) : coManagerEntries.length === 0 ? (
            <p className="text-[12px] text-slate-500 text-center py-2">
              No co-managers yet.
            </p>
          ) : (
            coManagerEntries.map((cm, idx) => {
              const isSelf =
                (cm.isUUIDBased && cm.id === accountUUID)
                || (cm.email && cm.email.toLowerCase() === (userEmail || '').toLowerCase());
              const isEntryOwner = cm.isUUIDBased && ownerUUIDs.includes(cm.id);

              // For UUID-keyed entries, name + email come from
              // /user/getEmailForControllerUUID and arrive after a tick.
              // Skip rendering until we've resolved at least one of them
              // — flashing a generic "Co-Manager" placeholder is uglier
              // than waiting half a frame.
              const isResolved = cm.isPending || isSelf || cm.email != null || cm.name != null;
              if (!isResolved) return null;

              // Compose a single bold line + optional sub-line. When only
              // the email is known we render it once, bold, with no
              // sub-line — never duplicate the same string on both rows.
              let displayName;
              let displaySub;
              if (cm.isPending) {
                displayName = cm.email;
                displaySub = null;
              } else if (isSelf) {
                displayName = 'You';
                displaySub = userEmail || null;
              } else if (cm.name) {
                displayName = cm.name;
                displaySub = cm.email || null;
              } else {
                displayName = cm.email;
                displaySub = null;
              }
              return (
                <CoManagerRow
                  key={cm.id}
                  divider={idx > 0}
                  name={displayName}
                  email={displaySub}
                  youTone={isSelf && !cm.isPending}
                  badge={
                    cm.isPending
                      ? <HBadge variant="anotherHome">Pending</HBadge>
                      : isEntryOwner
                        ? <HBadge variant="owner">Owner</HBadge>
                        : <HBadge variant="comanager">Co-Manager</HBadge>
                  }
                  rightSlot={
                    cm.isPending ? (
                      <HGhostButton
                        onClick={() => cancelCoManagerInvite(cm.email)}
                        className="text-[11px] py-1 px-2 text-slate-500"
                      >
                        Cancel
                      </HGhostButton>
                    ) : isSelf && !isEntryOwner ? (
                      <HGhostButton
                        onClick={giveUpControl}
                        disabled={isOnlyController}
                        className="text-[11px] py-1 px-2 text-slate-500"
                        title={isOnlyController ? 'You\u2019re the only controller.' : undefined}
                      >
                        Give up
                      </HGhostButton>
                    ) : null
                  }
                />
              );
            })
          )}
        </HCard>
        {coManagerError && (
          <p className="text-[12px] text-red-600 mb-3">{coManagerError}</p>
        )}

        {/* Actions */}
        <SectionLabel className="mb-2">Actions</SectionLabel>
        <HCard className="mb-4 overflow-hidden">
          {(() => {
            // Gating mirrors OLD DeviceSettings "Hand Control Training"
            // card, with training-pending semantics the user spelled out:
            //   - Auto Mode on → disabled (hand control is off anyway).
            //   - `trainingComplete !== true` → queued. Missing field and
            //     explicit `false` both mean "not confirmed done yet";
            //     the row stays disabled until the device flips it to
            //     `true`.
            // Offline is NOT a gate: the `trainingComplete: false` write
            // just waits on familyInfo until the Hearth comes back online
            // and picks it up. We show a hint in the subtitle so the user
            // knows what'll happen.
            const autoMode = family.autoMode === true;
            const trainingPending = family.trainingComplete !== true;
            const trainingDisabled = autoMode || trainingPending;
            const subtitle = autoMode
              ? 'Not available while Auto Mode is on'
              : trainingPending
                ? 'Pending — training is queued on the Hearth'
                : family.isOnline
                  ? 'Walks through the gestures again on the TV'
                  : 'Starts next time the Hearth is online';
            return (
              <ActionRow
                title="Repeat hand-control training"
                subtitle={subtitle}
                divider
                disabled={trainingDisabled}
                onClick={trainingDisabled ? undefined : handleRepeatTraining}
              />
            );
          })()}
          <ActionRow
            title="Device settings"
            subtitle={
              family.isOnline
                ? 'Feedback strip, lights, camera, TV behaviour'
                : family.hasEverSignedIn
                  ? 'Editable now · applied when it’s back online'
                  : 'Editable now · applied on first run'
            }
            onClick={() => navigate(`/app/hearths/${familyID}/settings`)}
          />
        </HCard>

        {/* Higlos this Hearth is in. Always shown when there are any —
            offline Hearths can still have their position reconfigured;
            the write lands on the family doc and the device picks it up
            on next boot. */}
        {family.iglooLinks?.length > 0 && (
          <>
            <div className="flex items-end justify-between mb-2">
              <SectionLabel>Higlos this Hearth is in</SectionLabel>
              <HGhostButton
                onClick={() => navigate('/app/higlos')}
                className="text-[11px] py-1 px-2"
              >
                Manage all →
              </HGhostButton>
            </div>
            <HCard className="mb-2">
              {sortByPosition(family.iglooLinks).map((link, idx, arr) => (
                <HigloPositionRow
                  key={link.iglooID}
                  link={link}
                  divider={idx < arr.length - 1}
                  onClick={() =>
                    navigate(`/app/higlos/${link.iglooID}/position/${familyID}`)
                  }
                />
              ))}
            </HCard>
            <p className="text-[11px] text-slate-500 leading-relaxed text-center mb-4">
              Tap a Higlo to change where this Hearth appears in it.{' '}
              <strong>&ldquo;Manage all&rdquo;</strong> takes you to the Higlos tab.
            </p>
          </>
        )}

        {/* SMS Higlo notifications opt-in — per family (per Hearth). Placed
            below the Higlos section; shown even when the Hearth isn't in any
            Higlo yet, so people can opt in ahead of joining one. */}
        <SmsNotificationsCard
          familyID={familyID}
          accountUUID={accountUUID}
          userEmail={userEmail}
        />

        {/* Hearths this Hearth is connected to. Membership is derived from
            the iglooInfo docs of every Higlo this Hearth is in. The label
            shown is this Hearth's per-perspective label
            (`familyInfo/{familyID}/connectedFamilies/{otherFID}.name`); if
            it's missing we fall through to the other Hearth's global name.
            Renames here only affect this Hearth's view of the other
            Hearth. */}
        {connectedFamilyIDs.length > 0 && (
          <>
            <SectionLabel className="mb-2">
              Hearths this Hearth is connected to
            </SectionLabel>
            <HCard className="mb-2">
              {connectedFamilyIDs.map((otherFID, idx) => {
                const globalName = connectedGlobalNames[otherFID] || '';
                const label = thisFamilyConnectedLabels[otherFID] || '';
                const isEditing = editingConnectedFID === otherFID;
                const hasCustomLabel = label && label !== globalName;
                const isLast = idx === connectedFamilyIDs.length - 1;
                return (
                  <div
                    key={otherFID}
                    className={cn(
                      'px-4 py-3 flex items-start gap-3',
                      !isLast && 'border-b border-slate-100',
                    )}
                  >
                    <HearthIcon size="xs" className="mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-medium text-slate-900 leading-tight">
                        {globalName || '…'}
                      </div>
                      {isEditing ? (
                        <div className="mt-2">
                          <input
                            value={pendingConnectedLabel}
                            onChange={(e) => setPendingConnectedLabel(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveConnectedRename();
                              if (e.key === 'Escape') cancelConnectedRename();
                            }}
                            disabled={savingConnectedLabel}
                            maxLength={40}
                            autoFocus
                            className="w-full text-[13px] text-slate-900 bg-white border border-slate-300 rounded px-2 py-1.5 outline-none focus:border-slate-900"
                          />
                          <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                            Referred to by this Hearth only.
                          </p>
                          <div className="flex justify-end gap-2 mt-2">
                            <HGhostButton
                              onClick={cancelConnectedRename}
                              disabled={savingConnectedLabel}
                              className="text-[12px] px-3"
                            >
                              Cancel
                            </HGhostButton>
                            <HPrimaryButton
                              onClick={saveConnectedRename}
                              disabled={savingConnectedLabel}
                              className="min-h-0 py-1.5 px-3 text-[12px]"
                            >
                              {savingConnectedLabel ? 'Saving…' : 'Save'}
                            </HPrimaryButton>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-0.5 text-[12px] text-slate-500 leading-snug">
                          {hasCustomLabel ? (
                            <>
                              Known as <span className="text-slate-700">{label}</span>
                            </>
                          ) : (
                            <>Known as this name</>
                          )}
                        </div>
                      )}
                    </div>
                    {!isEditing && (
                      <HGhostButton
                        onClick={() => beginConnectedRename(otherFID)}
                        className="text-[11px] py-1 px-2 flex-shrink-0"
                      >
                        Rename
                      </HGhostButton>
                    )}
                  </div>
                );
              })}
            </HCard>
            <p className="text-[11px] text-slate-500 leading-relaxed text-center mb-4">
              Renames here only change what this Hearth shows. Other Hearths keep their own names.
            </p>
          </>
        )}

        {/* Factory reset — any controller (owner or invited co-manager)
            can trigger it, matching OLD IndividualDashboard.FamilyRow. The
            button is disabled when the Hearth has been online before but
            is currently unreachable: the signoutRequested flag has
            nowhere to land until it's back online. */}
        <HSecondaryButton
          onClick={() => !resetDisabled && setShowResetDialog(true)}
          disabled={resetDisabled}
          className="w-full text-[13px] text-slate-500"
          style={{ paddingTop: '10px', paddingBottom: '10px' }}
          title={resetDisabled ? resetDisabledReason : undefined}
        >
          Factory reset this Hearth
        </HSecondaryButton>
        {resetDisabled && (
          <p className="text-[11px] text-slate-500 leading-relaxed text-center mt-2">
            {resetDisabledReason}
          </p>
        )}
      </div>

      {autoModeConfirm && (
        <AutoModeConfirmDialog
          onCancel={() => setAutoModeConfirm(false)}
          onConfirm={() => applyAutoMode(true)}
          count={nonCentreIglooLinks.length}
          isWorking={savingAutoMode}
        />
      )}

      {showResetDialog && (
        <FactoryResetDialog
          onCancel={() => setShowResetDialog(false)}
          onConfirm={handleFactoryReset}
        />
      )}
    </DetailShell>
  );
}

/* ──────────────── Subcomponents ──────────────── */

/* Binary active/inactive status for the title row dot. Matches v3 mockup
   language: green when the Hearth is online + not asleep; grey otherwise.
   Text supplies the detail ("at home" / "in Higlo X" / "asleep" / etc.). */
function resolveHearthStatus(family) {
  if (family.hasEverSignedIn === false) {
    return { active: false, text: 'Not yet connected' };
  }
  if (!family.isOnline) {
    const ago = timeAgo(family.onlinePingMs);
    return { active: false, text: `Offline${ago ? ` · last seen ${ago}` : ''}` };
  }
  if (family.screen === 'sleep') {
    return { active: false, text: 'Asleep' };
  }
  const presence = resolvePresence(family.screen, family.iglooLinks);
  if (presence) {
    return { active: true, text: `In ${presence.name}` };
  }
  return { active: true, text: 'At home' };
}

/* Confirm modal for enabling Auto Mode when the Hearth has non-Centre
   positions set — those get deactivated as part of the flip, matching
   OLD IndividualDashboard.updateAutoMode. */
function AutoModeConfirmDialog({ count, onCancel, onConfirm, isWorking }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-xl ring-1 ring-slate-200 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <SerifHeading size="md" className="text-center mb-2">
          Turn on Auto Mode?
        </SerifHeading>
        <p className="text-[13px] text-slate-600 leading-relaxed mb-4 text-center max-w-[320px] mx-auto">
          Auto Mode sends this Hearth straight into its Centre Higlo.{' '}
          {count === 1 ? '1 Higlo that' : `${count} Higlos that`}{' '}
          {count === 1 ? 'isn\u2019t' : 'aren\u2019t'} on Centre will be
          deactivated on this Hearth. You can re-activate them later.
        </p>
        <HButtonPair>
          <HSecondaryButton onClick={onCancel} disabled={isWorking}>
            Cancel
          </HSecondaryButton>
          <HDestructiveButton onClick={onConfirm} disabled={isWorking}>
            {isWorking ? 'Applying\u2026' : 'Turn on'}
          </HDestructiveButton>
        </HButtonPair>
      </div>
    </div>
  );
}

function PreferenceRow({ title, subtitle, control, divider }) {
  return (
    <div
      className={cn(
        'px-4 py-3.5 flex items-start justify-between gap-3',
        divider && 'border-b border-slate-100',
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-slate-900">{title}</div>
        {subtitle && (
          <div className="text-[12px] text-slate-500 leading-relaxed mt-0.5">
            {subtitle}
          </div>
        )}
      </div>
      <div className="flex-shrink-0 mt-0.5">{control}</div>
    </div>
  );
}

function CoManagerRow({ name, email, badge, youTone, divider, rightSlot }) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 py-1.5',
        divider && 'border-t border-slate-100 mt-1 pt-3',
      )}
    >
      <Avatar name={name} youTone={youTone} />
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-slate-900 truncate">{name}</div>
        {email && (
          <div className="text-[11px] text-slate-500 truncate">{email}</div>
        )}
      </div>
      {badge}
      {rightSlot && (
        <div className="flex-shrink-0">{rightSlot}</div>
      )}
    </div>
  );
}

function Avatar({ name, youTone }) {
  const initials = (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
  return (
    <div
      className={cn(
        'w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-[13px] font-semibold',
        youTone ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-900',
      )}
    >
      {initials}
    </div>
  );
}

function ActionRow({ title, subtitle, divider, disabled, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'w-full px-4 py-3 flex items-center justify-between text-left gap-3 transition-colors',
        disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50',
        divider && 'border-b border-slate-100',
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-slate-900">{title}</div>
        <div className="text-[11px] text-slate-500">{subtitle}</div>
      </div>
      {!disabled && <Chevron />}
    </button>
  );
}

function HigloPositionRow({ link, divider, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full px-4 py-3 flex items-center gap-3 text-left transition-colors',
        'hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50',
        divider && 'border-b border-slate-100',
      )}
    >
      <HigloIcon size="sm" />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-slate-900 truncate">
          {link.name || 'Higlo'}
        </div>
        <div className="text-[11px] text-slate-500">
          {link.position
            ? <>Appears on the <strong>{positionWord(link.position)}</strong></>
            : 'Not on a position yet'}
        </div>
      </div>
      <PositionMini position={link.position} />
    </button>
  );
}

function PositionMini({ position }) {
  const slots = [
    { id: 'Left', match: position === 'Left' },
    { id: 'Centre', match: position === 'Centre' },
    { id: 'Right', match: position === 'Right' },
  ];
  return (
    <div
      className="inline-flex items-center gap-1 px-1.5 py-1 rounded border border-slate-200 bg-slate-50 flex-shrink-0"
      aria-label={position ? `Position ${position}` : 'No position'}
    >
      {slots.map((s) => (
        <span
          key={s.id}
          className={cn('w-[5px] h-[5px] rounded-full', s.match ? 'bg-slate-900' : 'bg-slate-200')}
        />
      ))}
    </div>
  );
}

function Chevron() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#94a3b8"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="flex-shrink-0"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

/* Pencil affordance on the device name — makes click-to-rename discoverable
   (PLAN wifi-change-webapp §4.1). */
function PencilIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    </svg>
  );
}

function positionWord(p) {
  return p ? p.toLowerCase() : '';
}

function sortByPosition(links) {
  const order = { Left: 1, Centre: 2, Right: 3 };
  return [...links].sort((a, b) => (order[a.position] || 99) - (order[b.position] || 99));
}

function resolvePresence(screen, iglooLinks = []) {
  if (!screen) return null;
  if (['home', 'loading', 'training', 'sleep'].includes(screen)) return null;
  const POSITION_RE = /-(Left|Centre|Right)$/;
  const iglooID = screen.replace(POSITION_RE, '');
  const link = iglooLinks.find((l) => l.iglooID === iglooID);
  return { iglooID, name: link?.name || 'a Higlo' };
}

function timeAgo(ms) {
  if (!ms) return null;
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
